import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import API from "../../../utils/API";
import { useThemeContext } from "../../../context/ThemeContext";
import Step2AssessmentSetup from "./Steps/Step2AssessmentSetup";
import Step3ExcelUpload from "./Steps/Step3ExcelUpload";
import Step4AssessmentMatrix from "./Steps/Step4AssessmentMatrix";
import Step5COPOArticulation from "./Steps/Step5COPOArticulation";
import Step6ReviewSubmit from "./Steps/Step6ReviewSubmit";
import CourseHeader from "./Shared/CourseHeader";
import AssessmentBanner from "./Shared/AssessmentBanner";
import FacultyHeader from "../FacultyHeader";
import { saveAs } from "file-saver";
import OutcomesSection from "./Shared/OutcomesSection";
import { PencilIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import FiltersSection from "../../DynamicComponents/CO-PO Attainment/FiltersSection";
import CourseList from "../../DynamicComponents/CO-PO Attainment/CourseList";
import AttainmentTable from "../../DynamicComponents/CO-PO Attainment/AttainmentTable";

// Color constants for consistent theming
const COLORS = {
  dark: {
    background: "#1E1E2E",
    cardBackground: "#0D1117",
    text: "#C9CCD1",
    border: "#22232B",
    inputText: "#EAEAEA",
    primary: "#569CD6",
    danger: "#D43F3F",
    success: "#4EC9B0",
    warning: "#FFD700",
  },
  light: {
    background: "#F4F5F7",
    cardBackground: "#FFFFFF",
    text: "#2D3A4A",
    border: "#D1D5DB",
    inputText: "#1F252E",
    primary: "#007BFF",
    danger: "#E63946",
    success: "#28A745",
    warning: "#FFC107",
  },
};

const COPOAttainmentTool = () => {
  const { darkMode } = useThemeContext();
  const user = useSelector((state) => state.auth.user);

  // State for course filtering and selection
  const currentYear = new Date().getFullYear();
  // Add this state variable with the other state declarations
  const [assessmentMatrixData, setAssessmentMatrixData] = useState(null);
  const defaultAcademicYear = `${currentYear - 1}-${currentYear.toString().slice(-2)}`;
  const [academicYear, setAcademicYear] = useState(defaultAcademicYear);
  const [semesterType, setSemesterType] = useState("even");
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCourseList, setShowCourseList] = useState(true);

  // Academic years in YYYY-YY format
  const academicYears = [
    defaultAcademicYear,

    `${currentYear}-${(currentYear + 1).toString().slice(-2)}`,
    `${currentYear + 1}-${(currentYear + 2).toString().slice(-2)}`,
  ];

  // Original state
  const [activeTab, setActiveTab] = useState("current");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [currentCourses, setCurrentCourses] = useState([]);
  const [pastCourses, setPastCourses] = useState([]);
  const [courseOutcomes, setCourseOutcomes] = useState([]);
  const [programOutcomes, setProgramOutcomes] = useState([]);
  const [programSpecificOutcomes, setProgramSpecificOutcomes] = useState([]);
  const [articulationMatrix, setArticulationMatrix] = useState(null);
  const [assessmentSetup, setAssessmentSetup] = useState({
    assignments: 3,
    classTests: 2,
    projects: 1,
    includePRS: false,
  });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [assessmentMatrix, setAssessmentMatrix] = useState([]);
  const [coPoArticulation, setCoPoArticulation] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [hasAttainmentData, setHasAttainmentData] = useState(false);
  const [attainmentData, setAttainmentData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [existingIndirectScores, setExistingIndirectScores] = useState(null);

  // Add these state variables to FacultyAttainment.jsx
  const [existingAssessmentFile, setExistingAssessmentFile] = useState(null);
  const [existingAssessmentMatrix, setExistingAssessmentMatrix] =
    useState(null);
  const [existingArticulationMatrix, setExistingArticulationMatrix] =
    useState(null);
  const [isLoadingExistingData, setIsLoadingExistingData] = useState(false);
  // Add these state variables to FacultyAttainment.jsx
  // const [stepData, setStepData] = useState({
  //   step2: null,
  //   step3: null,
  //   step4: null,
  //   step5: null,
  // });

  // Add this useEffect to fetch existing data when entering edit mode
  useEffect(() => {
    const fetchExistingData = async () => {
      if (editMode && selectedCourse && academicYear) {
        setIsLoadingExistingData(true);
        try {
          // Fetch existing assessment matrix
          const matrixResponse = await API.get(
            "ece/faculty/assessment-matrix",
            {
              params: {
                course_code: selectedCourse.course_code,
                section_name: selectedCourse.section_name,
                semester: selectedCourse.semester,
                academic_year: academicYear,
              },
            }
          );

          if (matrixResponse.data && matrixResponse.data.assessment_tools) {
            setExistingAssessmentMatrix(matrixResponse.data.assessment_tools);

            // Extract assessment setup from the matrix
            const tools = matrixResponse.data.assessment_tools;
            const assignments = tools.filter((t) =>
              t.tool_name.startsWith("A")
            ).length;
            const classTests = tools.filter((t) =>
              t.tool_name.startsWith("CT")
            ).length;
            const projects = tools.filter(
              (t) => t.tool_name.startsWith("P") && t.tool_name !== "PRS"
            ).length;
            const includePRS = tools.some((t) => t.tool_name === "PRS");

            setAssessmentSetup({
              assignments,
              classTests,
              projects,
              includePRS,
            });
          }

          // Fetch existing articulation matrix
          const articulationResponse = await API.get(
            "ece/faculty/co-po-pso-matrix",
            {
              params: {
                course_code: selectedCourse.course_code,
                academic_year: academicYear,
              },
            }
          );

          if (articulationResponse.data) {
            setExistingArticulationMatrix(articulationResponse.data);
          }

          // Set uploaded file info (we'll handle the actual file download separately)
          setUploadedFile({
            name: `${selectedCourse.course_code}_marks.xlsx`,
            size: 0, // We don't have the actual size from the API
            isExisting: true,
          });

          const indirectResponse = await API.get(
            "ece/faculty/indirect-scores",
            {
              params: {
                course_code: selectedCourse.course_code,
                section_name: selectedCourse.section_name,
                academic_year: academicYear,
                semester_type: selectedCourse.semester_type || "even",
              },
            }
          );

          if (indirectResponse.data && indirectResponse.data.data) {
            setExistingIndirectScores(indirectResponse.data.data);
          }
        } catch (error) {
          console.error("Error fetching existing data:", error);
          toast.error("Failed to load existing attainment data");
        } finally {
          setIsLoadingExistingData(false);
        }
      }
    };

    fetchExistingData();
  }, [editMode, selectedCourse, academicYear]);

  // Add a function to download the existing assessment file
  const downloadExistingAssessmentFile = async () => {
    try {
      const response = await API.get("ece/faculty/assessment/sheet", {
        params: {
          course_code: selectedCourse.course_code,
          section_name: selectedCourse.section_name,
          semester: selectedCourse.semester,
          academic_year: academicYear,
        },
        responseType: "blob",
      });

      // Create a blob from the response and create a download link
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${selectedCourse.course_code}_marks.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Existing assessment file downloaded");
    } catch (error) {
      console.error("Error downloading assessment file:", error);
      toast.error("Failed to download existing assessment file");
    }
  };

  const colors = darkMode ? COLORS.dark : COLORS.light;
  // // Function to update step data
  // const updateStepData = (step, data) => {
  //   setStepData((prev) => ({
  //     ...prev,
  //     [step]: data,
  //   }));
  // };

  // // Function to handle step navigation with data persistence
  // const handleStepChange = (newStep) => {
  //   setCurrentStep(newStep);
  // };
  // Check if attainment data is actually populated (not just empty arrays)
  const hasActualAttainmentData =
    attainmentData &&
    (attainmentData.coAttainment?.length > 0 ||
      attainmentData.poAttainment?.length > 0 ||
      attainmentData.psoAttainment?.length > 0);

  // Fetch courses based on academic year and semester type
  const fetchCourses = async () => {
    if (!academicYear || !semesterType) {
      toast.error("Please select both academic year and semester type");
      return;
    }

    setIsLoading(true);
    try {
      const response = await API.get("/ece/faculty/courses", {
        params: {
          academic_year: academicYear,
          semester_type: semesterType,
          department_id: user.department_id,
          faculty_id: user.faculty_id, // Added faculty_id parameter
        },
      });

      if (response.data && response.data.subjects) {
        const flattenedCourses = Object.values(response.data.subjects).flat();
        setCourses(flattenedCourses);

        if (flattenedCourses.length === 0) {
          toast.error("No courses found for the selected criteria");
        } else {
          toast.success("Courses fetched successfully");
        }
      } else {
        setCourses([]);
        toast.error("No courses found for the selected criteria");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error(error.response?.data?.message || "Failed to fetch courses");
      setCourses([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch course data when a course is selected
  useEffect(() => {
    if (!selectedCourse || !academicYear) return;

    const fetchCourseData = async () => {
      try {
        // Check attainment status first
        const attainmentStatus = await API.get(
          "ece/faculty/attainment-status",
          {
            params: {
              course_code: selectedCourse.course_code,
              section_name: selectedCourse.section_name,
              semester_type: selectedCourse.semester_type,
              academic_year: academicYear,
            },
          }
        );

        if (attainmentStatus.data) {
          setAttainmentData(attainmentStatus.data);
          // Check if attainment data is actually populated
          const hasData =
            attainmentStatus.data.coAttainment?.length > 0 ||
            attainmentStatus.data.poAttainment?.length > 0 ||
            attainmentStatus.data.psoAttainment?.length > 0;
          setHasAttainmentData(hasData);
          setEditMode(false); // Reset edit mode when changing courses
        }

        // Fetch course outcomes
        const coResponse = await API.get("ece/faculty/course-outcomes", {
          params: {
            course_code: selectedCourse.course_code,
            academic_year: academicYear,
          },
        });
        setCourseOutcomes(coResponse.data.course_outcomes);

        // Fetch program outcomes
        const poResponse = await API.get("ece/faculty/program-outcomes", {
          params: {
            program_code: selectedCourse.program_code,
            academic_year: academicYear,
          },
        });
        setProgramOutcomes(poResponse.data.program_outcomes);

        // Fetch program specific outcomes
        const psoResponse = await API.get(
          "ece/faculty/program-specific-outcomes",
          {
            params: {
              program_code: selectedCourse.program_code,
              academic_year: academicYear,
            },
          }
        );
        setProgramSpecificOutcomes(psoResponse.data.program_specific_outcomes);

        // Fetch articulation matrix
        try {
          const articulationResponse = await API.get(
            "ece/faculty/co-po-pso-matrix",
            {
              params: {
                course_code: selectedCourse.course_code,
                academic_year: academicYear,
              },
            }
          );
          setArticulationMatrix(articulationResponse.data);
        } catch (error) {
          // If no articulation matrix exists, set to null
          if (error.response?.status === 404) {
            setArticulationMatrix(null);
            console.log("No articulation matrix found for this course");
          } else {
            throw error;
          }
        }
      } catch (error) {
        toast.error("Failed to load course data");
        console.error("Error fetching course data:", error);
      }
    };

    fetchCourseData();
    setCurrentStep(1); // Reset to first step when course changes
  }, [selectedCourse, academicYear]);

  const generateTemplate = async () => {
    if (!selectedCourse) return;

    try {
      const response = await API.post(
        "ece/faculty/assessment/template",
        {
          numAssignments: assessmentSetup.assignments,
          numCTs: assessmentSetup.classTests,
          numProjects: assessmentSetup.projects,
          hasPRS: assessmentSetup.includePRS,
          course_code: selectedCourse.course_code,
        },
        { responseType: "blob" }
      );

      const filename = `COPO_Template_${selectedCourse.course_code}_${assessmentSetup.assignments}A_${assessmentSetup.classTests}CT_${assessmentSetup.projects}P_${
        assessmentSetup.includePRS ? "PRS1" : "PRS0"
      }.xlsx`;

      saveAs(response.data, filename);
      toast.success("Template downloaded successfully");
    } catch (error) {
      toast.error("Failed to generate template");
      console.error("Error generating template:", error);
    }
  };

  const handleFileUpload = (file) => {
    setUploadedFile(file);
    toast.success("File ready for upload");
    setCurrentStep(3); // Move to next step without uploading
  };

  const handleAssessmentMatrixSubmit = (matrix) => {
    setAssessmentMatrix(matrix);
    setCurrentStep(4); // Move to CO-PO articulation step
  };

  const handleCoPoArticulationSubmit = (articulationData) => {
    // articulationData now contains both co_po_matrix and co_pso_matrix
    setCoPoArticulation(articulationData);
    setCurrentStep(5); // Move to review step
  };
  const submitAttainmentData = async () => {
    if (!uploadedFile) {
      toast.error("Please upload an Excel file first");
      return;
    }

    setIsSubmitting(true);
    try {
      // 2. Save CO-PO articulation matrix if it exists
      await API.post("ece/faculty/assessment-matrix", {
        section_name: selectedCourse.section_name,
        faculty_id: user.faculty_id,
        course_id: selectedCourse.course_id,
        academic_year: academicYear,
        assessment_tools: assessmentMatrix,
      });
      await API.post("ece/faculty/co-po-pso-matrix", {
        course_code: selectedCourse.course_code,
        academic_year: academicYear,
        co_po_matrix: coPoArticulation.co_po_matrix, // Updated
        co_pso_matrix: coPoArticulation.co_pso_matrix, // Updated
        program_code: selectedCourse.program_code,
        faculty_id: user.faculty_id,
        section_name: selectedCourse.section_name,
        semester: selectedCourse.semester,
      });
      await API.post("ece/faculty/indirect-scores", {
        course_code: selectedCourse.course_code,
        section_name: selectedCourse.section_name,
        academic_year: academicYear,
        semester_type: selectedCourse.semester_type || "even",
        co_indirect_scores: coPoArticulation.indirect_scores.map((score) => ({
          co_id: score.co_id,
          indirect_score: score.indirect_score,
        })),
      });
      // 1. Upload the Excel file with all required fields
      const formData = new FormData();
      formData.append("course_code", selectedCourse.course_code);
      formData.append("section_name", selectedCourse.section_name);
      formData.append("semester", selectedCourse.semester);
      formData.append("academic_year", academicYear);
      formData.append("uploaded_by", user.faculty_id); // Add faculty ID as uploaded_by
      formData.append("document", uploadedFile);

      await API.post("ece/faculty/assessment/upload-excel", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // 3. Calculate direct CO attainment
      await API.post("ece/faculty/directco/calculate", {
        course_code: selectedCourse.course_code,
        section_name: selectedCourse.section_name,
        academic_year: academicYear,
        semester_type: selectedCourse.semester_type,
      });

      // 4. Fetch calculated attainment data
      const attainmentResponse = await API.get(
        "ece/faculty/attainment-status",
        {
          params: {
            course_code: selectedCourse.course_code,
            section_name: selectedCourse.section_name,
            semester_type: selectedCourse.semester_type,
            academic_year: academicYear,
          },
        }
      );

      setAttainmentData(attainmentResponse.data);

      // Check if attainment data is actually populated
      const hasData =
        attainmentResponse.data.coAttainment?.length > 0 ||
        attainmentResponse.data.poAttainment?.length > 0 ||
        attainmentResponse.data.psoAttainment?.length > 0;
      setHasAttainmentData(hasData);

      setEditMode(false); // Exit edit mode after submission
      toast.success("CO-PO attainment calculated successfully");
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to complete attainment calculation"
      );
      console.error("Error in attainment calculation:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleEditMode = () => {
    setEditMode(!editMode);
    if (!editMode) {
      setCurrentStep(1); // Reset to step 1 when entering edit mode
    }
  };

  // Determine semester type display name
  const getSemesterTypeDisplayName = (type) => {
    return type === "odd" ? "Odd Semester" : "Even Semester";
  };

  // Go back to course selection
  const handleBackToCourses = () => {
    setShowCourseList(true);
    setSelectedCourse(null);
    setCurrentStep(1);
    setEditMode(false);
  };

  // Fetch courses on initial load
  useEffect(() => {
    fetchCourses();
  }, []);

  return (
    <div
      className="pb-4 min-h-screen"
      style={{ backgroundColor: colors.background }}
    >
      <FacultyHeader />
      <div className="flex mt-4 min-h-screen">
        {/* Main Content */}
        <div className="flex-1 overflow-y-auto px-4 mb-10 pb-10">
          {showCourseList ? (
            <div className="mt-4">
              {/* Filters Section */}
              <FiltersSection
                darkMode={darkMode}
                academicYear={academicYear}
                setAcademicYear={setAcademicYear}
                academicYears={academicYears}
                semesterType={semesterType}
                setSemesterType={setSemesterType}
                isLoading={isLoading}
                fetchCourses={fetchCourses}
                defaultAcademicYear={defaultAcademicYear}
              />

              {/* Course List */}
              {courses.length > 0 && (
                <CourseList
                  darkMode={darkMode}
                  courses={courses}
                  semesterType={semesterType}
                  getSemesterTypeDisplayName={getSemesterTypeDisplayName}
                  fetchCourseSections={(course) => {
                    setSelectedCourse(course);
                    setShowCourseList(false);
                  }}
                />
              )}

              {/* No Courses Message */}
              {courses.length === 0 && !isLoading && (
                <div
                  className="rounded-lg p-6 text-center border"
                  style={{
                    backgroundColor: darkMode ? "#0D1117" : "#F9FAFB",
                    borderColor: darkMode ? "#22232B" : "#D1D5DB",
                  }}
                >
                  <p style={{ color: darkMode ? "#B0B3B8" : "#4B5563" }}>
                    No courses found. Please adjust your filters and try again.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-4">
              <CourseHeader
                course={selectedCourse}
                onBack={handleBackToCourses}
                darkMode={darkMode}
              />

              {/* Only show edit button if there's actual attainment data */}
              {hasActualAttainmentData && (
                <button
                  onClick={toggleEditMode}
                  className="flex items-center gap-2 px-4 py-2 rounded-md border-2 font-medium text-sm transition-all duration-300 mb-4"
                  style={{
                    borderColor: editMode ? colors.danger : colors.primary,
                    color: editMode ? colors.danger : colors.primary,
                    backgroundColor: "transparent",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = editMode
                      ? "rgba(212, 63, 63, 0.1)"
                      : "rgba(86, 156, 214, 0.1)")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "transparent")
                  }
                >
                  {editMode ? (
                    <>
                      <LockClosedIcon className="h-4 w-4" />
                      Cancel Edit
                    </>
                  ) : (
                    <>
                      <PencilIcon className="h-4 w-4" />
                      Edit Attainment
                    </>
                  )}
                </button>
              )}

              {/* Show attainment table only if there's actual attainment data and not in edit mode */}
              {hasActualAttainmentData && !editMode && (
                <div className="mb-6">
                  <AttainmentTable data={attainmentData} darkMode={darkMode} />
                </div>
              )}

              {/* Show assessment workflow if:
                  1. There's no attainment data, OR
                  2. We're in edit mode */}
              {(!hasActualAttainmentData || editMode) && (
                <>
                  {currentStep >= 2 && (
                    <AssessmentBanner
                      assessmentSetup={assessmentSetup}
                      darkMode={darkMode}
                    />
                  )}

                  {isLoadingExistingData && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                      <div
                        className={`p-6 rounded-lg ${darkMode ? "bg-gray-800" : "bg-white"} flex items-center`}
                      >
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600 mr-3"></div>
                        <span
                          className={darkMode ? "text-white" : "text-gray-800"}
                        >
                          Loading existing attainment data...
                        </span>
                      </div>
                    </div>
                  )}
                  {/* // Update the step components to pass the existing data */}
                  {currentStep === 1 && (
                    <Step2AssessmentSetup
                      assessmentSetup={assessmentSetup}
                      courseOutcomes={courseOutcomes}
                      setAssessmentSetup={setAssessmentSetup}
                      onGenerateTemplate={generateTemplate}
                      onBack={() => {
                        setSelectedCourse(null);
                        setEditMode(false);
                      }}
                      onNext={() => setCurrentStep(2)}
                      darkMode={darkMode}
                      initialData={
                        editMode
                          ? {
                              assignments: assessmentSetup.assignments,
                              classTests: assessmentSetup.classTests,
                              projects: assessmentSetup.projects,
                              includePRS: assessmentSetup.includePRS,
                            }
                          : null
                      }
                    />
                  )}
                  {currentStep === 2 && (
                    <Step3ExcelUpload
                      uploadedFile={uploadedFile}
                      onFileUpload={handleFileUpload}
                      onBack={() => setCurrentStep(1)}
                      onNext={() => uploadedFile && setCurrentStep(3)}
                      darkMode={darkMode}
                      existingFile={editMode && uploadedFile?.isExisting}
                      onDownloadExisting={downloadExistingAssessmentFile}
                    />
                  )}
                  {currentStep === 3 && (
                    <Step4AssessmentMatrix
                      courseOutcomes={courseOutcomes}
                      assessmentSetup={assessmentSetup}
                      onBack={() => setCurrentStep(2)}
                      onNext={handleAssessmentMatrixSubmit}
                      darkMode={darkMode}
                      initialData={editMode ? existingAssessmentMatrix : null}
                      onDataChange={setAssessmentMatrixData}
                    />
                  )}
                  {currentStep === 4 && (
                    <Step5COPOArticulation
                      courseOutcomes={courseOutcomes}
                      programOutcomes={programOutcomes}
                      programSpecificOutcomes={programSpecificOutcomes}
                      onBack={() => setCurrentStep(3)}
                      onNext={handleCoPoArticulationSubmit}
                      darkMode={darkMode}
                      initialData={
                        editMode
                          ? {
                              ...existingArticulationMatrix,
                              indirect_scores: existingIndirectScores,
                            }
                          : null
                      }
                      selectedCourse={selectedCourse}
                      academicYear={academicYear}
                    />
                  )}
                  {/* Step 5: Review & Submit */}
                  {currentStep === 5 && (
                    <Step6ReviewSubmit
                      assessmentSetup={assessmentSetup}
                      onBack={() => setCurrentStep(4)}
                      onSubmit={submitAttainmentData}
                      isSubmitting={isSubmitting}
                      darkMode={darkMode}
                    />
                  )}
                </>
              )}

              {/* Permanent Outcomes Section - Always show */}
              <OutcomesSection
                outcomes={courseOutcomes}
                programOutcomes={programOutcomes}
                programSpecificOutcomes={programSpecificOutcomes}
                articulationMatrix={articulationMatrix}
                darkMode={darkMode}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default COPOAttainmentTool;
